/**
 * Event lifecycle status predicates.
 *
 * Kept as a dependency-free leaf module so pure domain code (for example
 * `domain/orderWorkspace/invoiceLocks`) can use it without pulling in the React
 * Query hooks that `domain/events/index` re-exports. `domain/events` re-exports
 * everything here, so UI code should keep importing from that public entrypoint.
 */

const normalise = (value: unknown): string => String(value ?? '').trim().toLowerCase();

/**
 * Statuses meaning the event is not going ahead. Distinct from
 * {@link APPROVAL_INERT_EVENT_STATUSES}, which also covers `invoiced` — an
 * invoiced event completed successfully and must not be described as terminal.
 */
export const TERMINAL_EVENT_STATUSES = ['cancelled', 'lost', 'void'] as const;

export type TerminalEventStatus = (typeof TERMINAL_EVENT_STATUSES)[number];

export const isTerminalEventStatus = (status: unknown): boolean =>
  (TERMINAL_EVENT_STATUSES as readonly string[]).includes(normalise(status));

/**
 * User-facing label for a terminal status, or null when the event is still
 * live. `void` reads as "Lost" to match the lifecycle tracker and stage filters.
 */
export const terminalEventStatusLabel = (status: unknown): string | null => {
  const value = normalise(status);
  if (value === 'cancelled') return 'Cancelled';
  if (value === 'lost' || value === 'void') return 'Lost';
  return null;
};

/**
 * Statuses after which an event's internal quote approvals are moot. A
 * cancelled, lost, void or invoiced event must never surface a pending approval
 * in anyone's queue.
 */
export const APPROVAL_INERT_EVENT_STATUSES = [
  ...TERMINAL_EVENT_STATUSES,
  'invoiced',
] as const;

export type ApprovalInertEventStatus = (typeof APPROVAL_INERT_EVENT_STATUSES)[number];

export const isApprovalInertEventStatus = (status: unknown): boolean =>
  (APPROVAL_INERT_EVENT_STATUSES as readonly string[]).includes(normalise(status));
